// ============================================
// 🧠 LEAD INSIGHTS — what a caller needs to know, worked out from the lead
// src/components/leadInsights.js
// Used by the Leads table and the phone cards, so both always agree.
// ============================================
const t = (v) => (v ? new Date(v).getTime() : 0);
const ago = (ms) => {
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${m % 60 ? `${m % 60} min` : ''}`.trim();
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'}`;
};
const clock = (d) => new Date(d).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
// A follow-up saved without a time (old data) sits at midnight UTC
const hasTime = (d) => { const x = new Date(d); return !(x.getUTCHours() === 0 && x.getUTCMinutes() === 0); };

// "Why should I call them now?" → { text, warn }
export function whyNow(lead, now = Date.now()) {
  if (!lead) return { text: '', warn: false };
  if (lead.status === 'converted') return { text: 'Converted', warn: false };
  if (lead.status === 'lost') return { text: 'Closed: not going ahead', warn: false };
  const lastUs = Math.max(t(lead.lastContactedAt), t(lead.lastOutboundAt), t(lead.lastAttemptAt));
  const lastIn = t(lead.lastInboundAt);
  if (lastIn && lastIn > lastUs && now - lastIn < 24 * 3600e3) return { text: `Replied ${ago(now - lastIn)} ago`, warn: true };
  if (lead.isUnattended) return { text: `Unattended, waiting ${ago(now - t(lead.createdAt))}`, warn: true };
  if (lead.cooldownUntil && t(lead.cooldownUntil) > now) return { text: `No answer, back at ${clock(lead.cooldownUntil)}`, warn: false };
  if (lead.nextFollowUpDate) {
    const f = t(lead.nextFollowUpDate), today = dayStart(now), fd = dayStart(f);
    const days = Math.round((today - fd) / 86400e3);
    if (fd < today) return { text: `Follow-up overdue ${days} day${days === 1 ? '' : 's'}`, warn: true };
    if (fd === today) return { text: `Follow-up due today${hasTime(f) ? `, ${clock(f)}` : ''}`, warn: !hasTime(f) || f <= now };
    if (fd === today + 86400e3) return { text: `Follow-up tomorrow${hasTime(f) ? `, ${clock(f)}` : ''}`, warn: false };
  }
  const reached = t(lead.lastContactedAt) || t(lead.lastOutboundAt);
  const age = now - t(lead.createdAt);
  if (!reached) {
    if ((lead.attemptCount || 0) > 0) return { text: `Not reached yet (${lead.attemptCount} ${lead.attemptCount === 1 ? 'try' : 'tries'})`, warn: age > 48 * 3600e3 };
    if (age < 2 * 60e3) return { text: 'New, just arrived', warn: false };
    if (age < 48 * 3600e3) return { text: `New, waiting ${ago(age)}`, warn: age > 3600e3 };
    return { text: `Never contacted (${ago(age)})`, warn: true };
  }
  const quiet = now - Math.max(reached, lastIn);
  if (quiet > 72 * 3600e3) return { text: `Quiet for ${ago(quiet)}`, warn: false };
  if (lead.nextFollowUpDate) return { text: `Follow-up ${new Date(lead.nextFollowUpDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}`, warn: false };
  return { text: 'Up to date', warn: false };
}

const OUTCOME = { interested: 'Interested', not_interested: 'Not interested', callback: 'Call back', no_answer: 'No answer', follow_up: 'Follow-up', converted: 'Converted', busy: 'Busy', connected: 'Connected' };

// "What happened last time?" → { text, by }
// ⚡ what a PERSON did (same rule as the server's lastOutcome): a call / meeting / visit, a typed WhatsApp
// reply ("messaged"), or a note with a real call result — system notes (imports, routing, Shark Tank) are skipped
const PERSON_OUTCOMES = ['interested', 'not_interested', 'callback', 'no_answer', 'follow_up', 'converted', 'busy', 'connected'];
function personAction(i) {
  if (!i) return null;
  const o = String(i.outcome || '');
  if (i.type === 'whatsapp') return o === 'follow_up' ? 'messaged' : (PERSON_OUTCOMES.includes(o) ? o : null);
  if (['call', 'meeting', 'visit'].includes(i.type)) return PERSON_OUTCOMES.includes(o) ? o : ((i.notes || o) ? 'other' : null);
  return PERSON_OUTCOMES.includes(o) && o !== 'follow_up' ? o : null;
}

export function lastOutcome(lead) {
  const list = (lead?.interactions || []).filter(i => personAction(i));   // ⚡ no system notes
  if (list.length) {
    const it = list.slice().sort((a, b) => t(b.date || b.createdAt) - t(a.date || a.createdAt))[0];
    const label = personAction(it) === 'messaged' ? 'Messaged on WhatsApp' : (OUTCOME[it.outcome] || '');
    const notes = String(it.notes || '').replace(/^Call attempt #\d+:\s*/i, '');
    const text = label && notes && !notes.toLowerCase().startsWith(label.toLowerCase()) ? `${label}: ${notes}` : (notes || label);
    const when = it.date || it.createdAt;
    const whenText = when ? new Date(when).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
    return { text, by: [it.createdByName, whenText].filter(Boolean).join(', ') };
  }
  if ((lead?.attemptCount || 0) > 0) return { text: `No answer (${lead.attemptCount} ${lead.attemptCount === 1 ? 'try' : 'tries'})`, by: '' };
  if (lead?.callStatus) return { text: lead.callStatus, by: '' };
  return { text: 'No calls yet', by: '' };
}

// "What do they want?"
export function interestOf(lead) {
  if (!lead) return '';
  const c = lead.clinic || {}, s = lead.school || {}, b = lead.business || {};
  if (lead.leadType === 'Clinic' || c.treatmentType || c.department) {
    const pkg = (c.packages || []).find(p => p && p.status !== 'Completed' && p.status !== 'Cancelled');
    return (pkg && pkg.packageName) || c.treatmentType || c.packageName || c.department || c.diagnosis || '';
  }
  if (lead.leadType === 'School' || s.gradeAppliedFor) return [s.gradeAppliedFor && `${s.gradeAppliedFor} admission`, s.studentName].filter(Boolean).join(', ');
  if (lead.leadType === 'Generic Business' || b.companyName) return [b.projectType, b.companyName && !['true', 'false'].includes(String(b.companyName).toLowerCase()) ? b.companyName : ''].filter(Boolean).join(', ');
  return '';
}

// Spender tier label, only when there is a real one
export function tierOf(lead) {
  const v = lead?.financial?.spenderCategory;
  if (!v || /^(none|no spend|no spend history|null|undefined)$/i.test(String(v))) return '';
  return String(v);
}

// "Meta lead ad" + "Hair transplant, October · Free consultation"
export function sourceCampaign(lead) {
  return { source: lead?.source || '', detail: [lead?.campaignName, lead?.adName].filter(Boolean).join(' · ') };
}